import { Component } from 'react';
import { inject, observer } from 'mobx-react';
import { cloneDeep } from 'lodash';
import { Row, Col } from 'react-bootstrap';
import queryString from 'query-string';
import moment from 'moment/min/moment-with-locales';
import { confirmAlert } from 'react-confirm-alert';
import { Modal, OverlayTrigger, Tooltip } from 'react-bootstrap';

//components
import applicationRouter from '~/hoc/applicationRouter';
import withLocalization from '~/hoc/withLocalization';
import AdminAddLimitBeforeAdd from './AdminAddLimitBeforeAdd';
import GenericForm from '~/components/GenericForm/GenericForm';

//elements
import LoadingSpinner from '~/elements/LoadingSpinner';
import Button from '../../../components/CustomButton/CustomButton.jsx';
import DatePicker from '../../../components/DatePicker/DatePicker';

//utils
import getSchema from '~/library/schemas/user';
import customerFeature from '~/utils/customerFeature';
import { getCountryCode } from '../../../utils/dateFormat';
import { payrollIntegrationOptions } from '../../../utils/constants';
import { DEFAULT_EXTRA_PAYMENTS } from '../../../utils/constants';
import { translateLabels } from '../../../utils/commonMethods';
import UserProjectAssignForm from './UserProjectAssignForm.js';
import UserPmAssignForm from './UserPmAssignForm.js';

//assets
import CloseModalIcon from '../../../assets/img/CloseApproveModalIcon.svg'

const defaultUiSchema = {
    'ui:field': 'layout',
    'ui:layout:hideframe': true,
    'ui:layout': [
        {
            'ui:subtitle': 'Member information',
            'ui:className': 'profile-edit-form__group',
            image: { xs: { span: 12, order: 1 }, sm: { span: 4, order: 1 }, md: 3 },
            first_name: { xs: { span: 6, order: 1 }, sm: { span: 4, order: 1 }, md: 3, className: "d-block d-sm-flex align-items-center mt-2 mt-md-1" },
            last_name: { xs: { span: 6, order: 1 }, sm: { span: 4, order: 1 }, md: 3, className: "d-block d-sm-flex align-items-center mt-2 mt-md-1" },
            status: { xs: { span: 4, offset: 1, order: 3 }, sm: { span: 2, offset: 0, order: 1 }, md: 2, className: "d-block d-sm-block d-md-flex align-items-center mt-2 mt-md-0" },
            email: { xs: { span: 12, order: 1 }, sm: { span: 6, order: 1 }, md: 3, className: "mt-2" },
            phone: { xs: { span: 6, order: 1 }, sm: { span: 4, order: 1 }, md: 2, className: "mt-2" },
            bank_account_number: { xs: { span: 6, order: 1 }, sm: { span: 6, order: 1 }, md: 3, className: "mt-2" },
            social_number: { xs: { span: 6, order: 2 }, sm: { span: 6, order: 1 }, md: 2, className: "mt-2" },
            birthday: { xs: { span: 6, order: 2 }, sm: { span: 6, order: 1 }, md: 2, className: "mt-2" },
        },
        {
            'ui:subtitle': 'Address information',
            'ui:className': 'profile-edit-form__group',
            address: { xs: 12, sm: 4, className: "mt-2" },
            post_number: { xs: 6, sm: 4, className: "mt-2" },
            post_place: { xs: 6, sm: 4, className: "mt-2" },
        },
        {
            'ui:subtitle': 'Access Information',
            'ui:className': 'profile-edit-form__group',
            internal_number: { xs: 3, sm: 4, className: "mt-2" },
            username: { xs: 6, sm: 4, className: "mt-2" },
            password: { xs: 3, sm: 4, className: "mt-2" },
            allow_mobile_app_access: { xs: 3, sm: 4, className: "mt-2" },
        },
        {
            'ui:subtitle': 'System settings',
            'ui:className': 'profile-edit-form__group',
            timelog_start_from: { xs: { span: 12, order: 1 }, sm: 4, className: "mt-2" },
            timelog_end_at: { xs: { span: 12, order: 1 }, sm: 4, className: "mt-2" },
            vacation_days: { xs: { span: 12, order: 1 }, sm: 4, className: "mt-2" },
            company_shift_id: { xs: { span: 12, order: 2 }, sm: { span: 4, order: 2 }, className: "mt-2" },
            employee_type: { xs: { span: 12, order: 3 }, sm: 4, md: 4, className: "mt-2" },
            groups: { xs: { span: 12, order: 3 }, sm: 4, md: 4, className: "mt-2" },
            leave_type_grants: { xs: { span: 12, order: 3 }, sm: 4, md: 4, className: "mt-2" },
            // allow_tip: { xs: { span: 6, order: 3 }, sm: 2, md: 2, className: "mt-2" }, // TIPS (hidden on frontend, backend module still active)
            allow_auto_flexitime: { xs: { span: 6, order: 3 }, sm: 2, md: 2, className: "mt-2" },
            max_negative_hours_flexitime: { xs: { span: 12, order: 3 }, sm: 4, md: 4, className: "mt-2" },
            max_positive_hours_flexitime: { xs: { span: 12, order: 3 }, sm: 4, md: 4, className: "mt-2" },
            user_skills: { xs: { span: 12, order: 3 }, sm: 4, md: 4, className: "mt-2" },
            exclude_break_in_working_hours_rule: { xs: { span: 12, order: 3 }, sm: 4, md: 4, className: "mt-2" },
            allow_user_for_auto_timesheet: { xs: { span: 12, order: 3 }, sm: 4, md: 4, className: "mt-2" },
        },
        {
            'ui:subtitle': 'Payment settings',
            'ui:className': 'profile-edit-form__group',
            payment_mode: { xs: { span: 12, order: 1 }, sm: { span: 4, order: 1 }, className: "mt-2" },
            hourly_rate: { xs: { span: 12, order: 1 }, sm: { span: 4, order: 1 }, className: "mt-2" },
            fixed_hourly_rate: { xs: { span: 12, order: 1 }, sm: { span: 4, order: 1 }, className: "mt-2" },
            payment_cycle: { xs: { span: 12, order: 4 }, sm: { span: 4, order: 1 }, className: "mt-2" },
            expected_fixed_hours: { xs: { span: 12, order: 2 }, sm: { span: 4, order: 1 }, className: "mt-2 me-md-2" },
            weekend_extra_payment_percent: { xs: { span: 12, order: 1 }, sm: { span: 4, order: 1 }, className: "mt-2" },
            calculated_weekly_hourly_payment: { xs: { span: 12, order: 3 }, sm: { span: 4, order: 1 }, className: "mt-2 me-md-2" },
            employee_percent: { xs: { span: 12, order: 1 }, sm: { span: 4, order: 1 }, className: "mt-2" },
            work_time_agreement: { xs: { span: 12, order: 3 }, sm: { span: 4, order: 2 }, md: 4, className: "mt-2" },
            calculated_employement_hours: { xs: { span: 12, order: 3 }, sm: { span: 4, order: 3 }, md: 4, className: "mt-2" },
            extra_payments: { xs: { span: 12, order: 3 }, sm: { span: 4 }, className: "mt-2" },
            auto_break: { xs: { span: 6, order: 3 }, sm: { span: 4, order: 3 }, md: 4, className: "mt-2" },
            follow_schedule_for_absence: { xs: { span: 6, order: 4 }, sm: { span: 4, order: 4 }, md: 4, className: "mt-2" },
            paid_overtime_renewal_hours: { xs: { span: 12, order: 4 }, sm: { span: 4, order: 4 }, md: 4, className: "mt-2" },
        },
        {
            'ui:className': 'profile-edit-form__group',
            attachments: { md: 12 },
        },
        {
            'ui:subtitle': 'Integration Details',
            'ui:className': 'profile-edit-form__group',
            departmentIntegration: { md: 4 }
        }
    ],

    status: {
        'ui:widget': 'ActiveInactive',
    },
    image: {
        'ui:widget': 'ImageUpload',
    },
    // allow_tip: { // TIPS (hidden on frontend, backend module still active)
    //     'ui:widget': 'Radio',
    // },
    disable_autolog: {
        'ui:widget': 'TrueFalse',
    },
    attachments: {
        'ui:widget': 'AttachmentsWidget',
        'ui:imageContext': {
            model: 'User',
            fileType: 'docs',
            id: 0,
        },
    },
    ManagesUsers: {
        items: {
            id: {
                'ui:widget': 'MembersWidget',
            },
        },
    },
    groups: {
        'ui:widget': 'SingleUserGroupsWidget',
    },
    allow_mobile_app_access: {
        'ui:widget': 'TrueFalse',
    },
    departmentIntegration: {
        'ui:widget': 'UserFormIntegrationWidget',
    },
    extra_payments: {
        'ui:widget': 'SimpleMultiSelectWidget',
        'ui:options': []
    },
    allow_auto_flexitime: {
        'ui:widget': 'TrueFalse',
    },
    auto_break: {
        'ui:widget': 'TrueFalse',
    },
    exclude_break_in_working_hours_rule: {
        'ui:widget': 'TrueFalse',
    },
    allow_user_for_auto_timesheet: {
        'ui:widget': 'TrueFalse',
    },
    follow_schedule_for_absence: {
        'ui:widget': 'TrueFalse',
    },
    user_skills: {
        'ui:widget': 'SingleSkillsWidget',
    },
    employee_type: {
        'ui:widget': 'DepartmentSelectWidget',
    },
    new_pm_id: {
        'ui:widget': 'PmReassignmentWidget',
    },
};

class UsersForm extends Component {
    constructor(props) {
        super(props);
        this.state = {
            schema: null,
            uiSchema: defaultUiSchema,
            assignedDocuments: [],
            showProjectModal: false,
            showPmModal: false,
            managerCount: 0,
            savedUserId: null,
            payment_mode: 'hourly',
            projectCount: 0,
            showRosterConfirmModal: false,
            showWorkHoursConfirmModal: false,
            requiresWorkHoursRulesConfirmation: false,
            pendingRosterValues: null,
            pendingImplementDate: null,
            showDatePicker: false,
            implementDateOfDepartmentChange: null,
        };
        this.setRef = this.setRef.bind(this);
        this.confirmSync = this.confirmSync.bind(this);
        this.isFormInitialized = false;
        this.lastAllowAutoTimesheet = false;
        this.lastEmployeeType = null;
        this.lastCompanyShiftId = null;
        props.userStore.startLoading();
    }

    setRef(button) {
        this.childRef = button;
    }

    async loadData() {
        const { userStore, commonStore, clientStore, mode, id, add } = this.props;
        const { currentEntity } = userStore;
        const dateTimeRules = commonStore.config.client && commonStore.config.client.data
            && commonStore.config.client.data.dateTimeRules
            ? commonStore.config.client.data.dateTimeRules : false;

        if (!add) {
            await userStore.load(id, add);
        }

        if (commonStore.config?.client?.allow_company_shifts) {
            await clientStore.getWorkHoursRule();
        }

        this.setState({
            schema: getSchema({
                config: this.props.commonStore.config,
                isAdd: add,
                mode,
                userType: userStore.currentUser.user_type,
                currentEntityUser: this.props.userStore?.currentEntity?.user || 'fixed',
                currentUser: this.props.userStore?.currentUser,
                translate: this.props.t,
                sortCompanyShifts: this.props.commonStore.sortCompanyShifts,
            }),
            payment_mode: currentEntity.user?.payment_mode || 'hourly'
        });
        this.lastAllowAutoTimesheet = !!userStore.currentEntity?.user?.allow_user_for_auto_timesheet;
        this.lastEmployeeType = userStore.currentEntity?.user?.employee_type || null;
        this.lastCompanyShiftId = userStore.currentEntity?.user?.company_shift_id || null;
        this.isFormInitialized = true;

        const params = queryString.parse(this.props.router.location.search) || {};
        this.setState({ uiSchema: this.prepareSchema(defaultUiSchema, currentEntity.user, mode, userStore.currentUser) });
        if (add) {
            if (mode !== 'superadmins') {
                await userStore.validateAddPossible();
            }

            let vacation_days = 25;
            if (
                commonStore.config &&
                commonStore.config.client &&
                commonStore.config.client.data &&
                commonStore.config.client.data.Vacation_setup &&
                commonStore.config.client.data.Vacation_setup.standared_vacation_days
            )
                vacation_days = commonStore.config.client.data.Vacation_setup.standared_vacation_days;

            const countryCode = getCountryCode(dateTimeRules);

            return userStore.returnDefaultNew(
                mode,
                userStore.currentUser.client_id,
                params,
                userStore.currentUser,
                vacation_days,
                countryCode
            );
        }
    }

    componentDidMount() {
        this.loadData();
    }

    updateProjectCount = (count) => {
        this.setState({ projectCount: count });
    }

    handleCloseModal = () => {
        const { t } = this.props;
        const { projectCount, savedUserId } = this.state;

        if (projectCount === 0) {
            confirmAlert({
                title: t('Confirm'),
                message: t("Are you sure you want to continue without assigning projects to this member?"),
                buttons: [
                    {
                        label: t('Yes'),
                        onClick: () => {
                            this.setState({ savedUserId: null });
                            this.getBack(savedUserId);
                        }
                    },
                    {
                        label: t('No'),
                        onClick: () => { }
                    }
                ]
            });
        } else {
            this.setState({ savedUserId: null });
            this.getBack(savedUserId);
        }
    }

    componentDidUpdate(previousProps) {
        const { location } = this.props;
        if (location !== previousProps.location) {
            this.loadData();
        }
    }

    addAssignedDocuments = async (user_id) => {
        const { documentStore } = this.props;
        let data = {
            document_template_ids: this.state.assignedDocuments,
            user_id: user_id,
        };
        await documentStore.assignDocumentTemplatesToUser(data);
    };

    addAssignedLeaveTypes = async (user_id) => {
        await this.props.userStore.saveUserLeaveTypeGrants(user_id, this.pendingLeaveTypeCodes || []);
    };

    employeeContract = () => {
        const { commonStore, t, id, add } = this.props;
        id === 0 ? this.childRef.click()
            :
            this.props.router.navigate('/admin/employeecontract', {
                state: { detail: id },
            });
        if (!add) commonStore.addNotification(t('Saved'), null, 'success');
    };

    validateUser(formData, errors) {
        const { t } = this.props;

        if (formData?.birthday && moment(formData?.birthday).isSameOrAfter(moment())) {
            errors.birthday.addError(t('Please enter valid birthdate'));
        }
        if (formData?.username && formData?.username.trim() === '') {
            errors.username.addError(t('is a required property'));
        }
        if (formData?.payment_cycle === 'monthly' && formData?.expected_fixed_hours > 743) {
            errors.expected_fixed_hours.addError(t('Max monthly overtime hours limit is 743'));
        }
        if (formData?.payment_cycle === 'weekly' && formData?.expected_fixed_hours > 167) {
            errors.expected_fixed_hours.addError(t('Max weekly overtime hours limit is 167'));
        }
        if (formData?.user_type !== 'super-admin' && (!formData?.employee_percent || isNaN(formData?.employee_percent) || formData?.employee_percent > 100 || formData?.employee_percent < 1)) {
            errors?.employee_percent?.addError(t("Employment percentage should be between 1 to 100"));
        }
        if (formData?.max_negative_hours_flexitime && formData?.max_negative_hours_flexitime > 0) {
            errors?.max_negative_hours_flexitime?.addError(t("Negative Flexitime can only be in Negative"));
        }
        if (formData?.max_positive_hours_flexitime && formData?.max_positive_hours_flexitime < 0) {
            errors?.max_positive_hours_flexitime?.addError(t("Positive Flexitime can only be in Positive"));
        }
        if (formData?.timelog_end_at && formData?.timelog_start_from && moment(formData.timelog_end_at).isBefore(moment(formData.timelog_start_from), 'day')) {
            errors?.timelog_end_at?.addError(t('End date must be on or after start date'));
        }
        return errors;
    }

    confirmSync = (values, result, add) => {
        const { commonStore, userStore, t } = this.props;
        if (commonStore.config?.integration_details?.product && values.user_type !== 'admin') {
            if (add || !values.int_id) {
                const str = `${t("Do You want to Sync Member with")} ${t(commonStore.config?.integration_details?.product)}?`;
                confirmAlert({
                    title: this.props.t('Confirm to Sync'),
                    message: str,
                    buttons: [
                        {
                            label: t('Yes'),
                            onClick: () => {
                                return userStore.saveIntoIntegration(result.user).then((res2) => {
                                    userStore.resetLastListLoadTime(new Date());
                                    commonStore.addNotification(`${t(res2.message)} ${commonStore.config?.integration_details?.product}`, null, 'success', "top-right", 3000);
                                    return this.redirectAfterSaveAndSycn();
                                }).catch(() => {
                                    this.props.onAddEdit(result.user.id)
                                });
                            },
                        },
                        {
                            label: t('No'),
                            onClick: () => {
                                return this.redirectAfterSaveAndSycn();
                            },
                        },
                    ],
                })
            }
            else {
                userStore.saveIntoIntegration(result.user).then((res2) => {
                    return this.redirectAfterSaveAndSycn();
                });
            }

        }
        else {
            return this.redirectAfterSaveAndSycn();
        }
    }

    redirectAfterSaveAndSycn = () => {
        this.setState({ savedUserId: null });
        if (this.props.goBack) this.getBack();
        else this.props.router.navigate(this.props.getListUrl());
    }

    handleSaveResult(result) {
        const { t, commonStore } = this.props;
        if (!result.user || !result.user.id) {
            commonStore.addNotification(t(result.message || 'Error'), null, 'error');
            return false;
        }
        if (result.user.id && result.user.status === 'active') {
            if (!customerFeature('hide_documents')) this.addAssignedDocuments(result.user.id);
            this.addAssignedLeaveTypes(result.user.id);
        }
        commonStore.addNotification(t('Saved'), null, 'success');

        this.proceedAfterSave(result);
    }

    proceedAfterSave(result) {
        if (this.props.add) {
            if (result.user.user_type === 'admin' || result.user.user_type === 'super-admin') this.getBack(result.user.id);
            //assigning a manager comes first, then projects//
            else this.setState({ showPmModal: true, savedUserId: result.user.id });
        } else {
            this.redirectAfterSaveAndSycn();
        }
        // isUserUpdatedValuesForVisma(commonStore, add, values, t, result, this.confirmSync);
    }

    updateManagerCount = (count) => {
        this.setState({ managerCount: count });
    }

    //closes the manager step and moves on to the project step//
    proceedToProjectStep = () => {
        this.setState({ showPmModal: false, showProjectModal: true });
    }

    handleClosePmModal = () => {
        const { t } = this.props;

        if (this.state.managerCount === 0) {
            confirmAlert({
                title: t('Confirm'),
                message: t("Are you sure you want to continue without assigning a manager to this member?"),
                buttons: [
                    { label: t('Yes'), onClick: () => this.proceedToProjectStep() },
                    { label: t('No'), onClick: () => { } }
                ]
            });
            return;
        }
        this.proceedToProjectStep();
    }

    onSave(values, redirect) {
        const { userStore, t, add, mode } = this.props;
        values.isAdminOrManager = true;

        this.pendingLeaveTypeCodes = Array.isArray(values.leave_type_grants)
            ? values.leave_type_grants
            : (values.leave_type_grants || '').split(',').filter(Boolean);
        delete values.leave_type_grants;

        //taking username from email if not provided//
        if (!values.username) values.username = values.email.split('@')[0];

        // if (!values.MemberInProjects && values.user_type !== 'admin' && values.user_type !== 'super-admin') {

        //     if (userStore.currentEntity.MemberInProjects && userStore.currentEntity.MemberInProjects.length > 0) {
        //         values.MemberInProjects = userStore.currentEntity.MemberInProjects;
        //     } else {
        //         commonStore.addNotification(t('Select projects for member'), null, 'error');
        //         return;
        //     }
        // }

        const originalEmployeeType = userStore.currentEntity?.user?.employee_type;
        const isDepartmentChanged = !add && values.employee_type && originalEmployeeType !== values.employee_type;

        if (isDepartmentChanged) {
            const proceedDepartmentChange = () => userStore.validateDepartmentChange(values.id, values)
                .then((result) => {
                    if (result && result.requiresRosterConfirmation) {
                        this.setState({
                            showRosterConfirmModal: true,
                            pendingRosterValues: values,
                            requiresWorkHoursRulesConfirmation: result.requiresWorkHoursRulesConfirmation || false,
                        });
                        return;
                    }
                    return userStore.save(values, add, mode)
                        .then((result) => this.handleSaveResult(result))
                        .catch(err => console.error(err));
                })
                .catch(err => console.error(err));

            return proceedDepartmentChange();
        }

        return userStore.save(values, add, mode).then((result) => {
            return this.handleSaveResult(result);
        }).catch(err => {
            console.error(err)
        });
    }

    clearPendingRosterState = () => {
        this.setState({ pendingRosterValues: null, pendingImplementDate: null, requiresWorkHoursRulesConfirmation: false, showDatePicker: false, implementDateOfDepartmentChange: null });
    }

    executeSave = (implementDateOfDepartmentChange, removeFutureShiftsForNoWorkRules = false) => {
        const { userStore, add, mode } = this.props;
        const { pendingRosterValues } = this.state;
        this.clearPendingRosterState();
        const savePayload = { ...pendingRosterValues, confirmRosterChange: true, implementDateOfDepartmentChange };
        if (removeFutureShiftsForNoWorkRules) savePayload.removeFutureShiftsForNoWorkRules = true;
        userStore.save(savePayload, add, mode)
            .then((result) => this.handleSaveResult(result))
            .catch(err => console.error(err));
    }

    handleRosterConfirm = (implementDateOfDepartmentChange = null) => {
        const { requiresWorkHoursRulesConfirmation } = this.state;
        this.setState({ showRosterConfirmModal: false, showDatePicker: false, implementDateOfDepartmentChange: null });

        if (requiresWorkHoursRulesConfirmation) {
            this.setState({ showWorkHoursConfirmModal: true, pendingImplementDate: implementDateOfDepartmentChange });
            return;
        }

        this.executeSave(implementDateOfDepartmentChange);
    }

    handleRosterCancel = () => {
        this.setState({ showRosterConfirmModal: false });
        this.clearPendingRosterState();
    }

    handleWorkHoursConfirm = () => {
        const { pendingImplementDate } = this.state;
        this.setState({ showWorkHoursConfirmModal: false });
        this.executeSave(pendingImplementDate, true);
    }

    handleWorkHoursCancel = () => {
        this.setState({ showWorkHoursConfirmModal: false });
        this.clearPendingRosterState();
    }

    //added getBack for calling updating parent//
    getBack(id = null) {
        const { page, userStore, id: propId, add } = this.props;
        const savedId = id || (!add && propId ? propId : null);
        if (savedId) userStore.setLastInteractedId(savedId);
        this.props.goBack(page);
    }

    prepareSchema(_schema, currentEntity, mode, currentUser) {
        const clientConfig = this.props.commonStore.config.client.data;
        const extra_payments_rules = clientConfig.extraPayments ? clientConfig.extraPayments : [];
        const autoTimelogsEnabled = clientConfig?.loginRules?.autoTimelogs === 'gps' || false;
        const isMobileAccessDisabled = clientConfig?.loginRules?.disableMobileAccess || false;
        const hasCompanyShifts = this.props.commonStore.config.client.allow_company_shifts;

        const schema = cloneDeep(_schema);
        schema.image = {
            'ui:widget': 'ImageUpload',
            'ui:imageContext': {
                model: 'User',
                fileType: 'logo',
                id: currentEntity ? currentEntity.id : 0,
            },
        };
        schema.attachments = {
            'ui:widget': 'AttachmentsWidget',
            'ui:imageContext': {
                model: 'User',
                fileType: 'docs',
                id: currentEntity ? currentEntity.id : 0,
                existingAttachment: currentEntity && currentEntity.Attachments ? currentEntity.Attachments : [],
                withTable: true,
            },
        };

        schema.leave_type_grants = {
            'ui:widget': 'UserLeaveTypeGrantsWidget',
            'ui:user_id': currentEntity ? currentEntity.id : 0,
        };

        schema.auto_break = {
            'ui:widget': 'TrueFalse',
            'ui:help': this.props.t("If disabled then break will be not deducted from timesheet and absence")
        }
        schema.exclude_break_in_working_hours_rule = {
            'ui:widget': 'TrueFalse',
            'ui:help': this.props.t("Break time is not counted in shift code's working hours")
        }
        schema.allow_user_for_auto_timesheet = {
            'ui:widget': 'TrueFalse',
            'ui:help': this.props.t("Automatically create timesheets according to schedule")
        }

        schema.follow_schedule_for_absence = {
            'ui:widget': 'TrueFalse',
            'ui:help': this.props.t("Uses primary shift or company hours when disabled.")
        }

        if (schema.extra_payments) {
            const allowUrgentWork = clientConfig.basicRules && clientConfig.basicRules.urgentWorkSettings && clientConfig.basicRules.urgentWorkSettings.allowUrgentWork;
            const urgentWorkRules = allowUrgentWork ? DEFAULT_EXTRA_PAYMENTS.filter(ele => ele.split("_").includes('urgent')) : [];
            const assignableExtraPayments = extra_payments_rules.filter(ele => ele.isActive
                && ((!ele.task && !ele.isMultiTask && !(DEFAULT_EXTRA_PAYMENTS.includes(ele.code)))
                    || (ele.task && urgentWorkRules.includes(ele.code))
                    || (ele.isMultiTask && urgentWorkRules.includes(ele.code))
                )
            );

            const multiTaskPaymentTaskIds = currentEntity?.multiTaskPaymentTaskIds || {};
            const memberTaskIds = (currentEntity?.MemberInTasks || '').split(',').filter(Boolean).map(Number);

            const extraPaymentOptions = assignableExtraPayments.map(ele => {
                let isDisabled;
                if (ele.isMultiTask) {
                    const taskIds = multiTaskPaymentTaskIds[ele.code] || [];
                    isDisabled = taskIds.length > 0 && !taskIds.some(taskId => memberTaskIds.includes(taskId));
                } else {
                    isDisabled = ele.task && !currentEntity?.MemberInTasks?.includes(ele.task);
                }
                return {
                    label: ele.name,
                    value: ele.code,
                    key: ele.name,
                    disabled: isDisabled,
                    disabledReason: isDisabled ? ` ( ${this.props.t("Not assigned to task")} )` : ''
                }
            });
            translateLabels(extraPaymentOptions, this.props.t, 'label');
            extraPaymentOptions.forEach(ele => {
                if (ele.disabled) {
                    ele.label += ele.disabledReason;
                }
            });
            schema.extra_payments = {
                'ui:widget': 'SimpleMultiSelectWidget',
                'ui:options': extraPaymentOptions
            }
        }
        if (mode === 'members') {
            const accessObj = {
                'ui:subtitle': 'Access Information',
                'ui:className': 'profile-edit-form__group',
            };
            accessObj.username = { xs: 12, sm: 4, className: "mt-1" };
            accessObj.password = { xs: 6, sm: 4, className: "mt-1" };
            accessObj.user_type = { xs: 6, sm: 4, className: "mt-1" };
            accessObj.internal_number = { xs: 6, sm: 4, className: "mt-1" };
            if (customerFeature('locked_mode')) {
                accessObj.generic_pin = { xs: 6, sm: 4, className: "mt-1" };
            }
            if (currentUser.role !== 'member' && autoTimelogsEnabled) {
                accessObj.disable_autolog = { xs: 6, sm: 4, md: 2, className: "mt-2" };
            }
            if (isMobileAccessDisabled)
                accessObj.allow_mobile_app_access = { xs: 6, sm: 4, md: 2, className: "mt-2" };
            schema['ui:layout'][2] = accessObj;

            if (!hasCompanyShifts) {
                delete schema['ui:layout'][3].company_shift_id;
            }
        }
        if (mode === 'managers') {
            schema['ui:layout'].push({
                ManagesUsers: { md: 12 },
            });
        }

        if (currentEntity?.user_type === 'pm') {
            //drop layout entries whose schema property was deleted above (e.g. allow_auto_flexitime, user_skills) -
            //LayoutField still renders an empty Col for them, which otherwise pushes new_pm_id onto its own row//
            const jsonSchemaProperties = this.state.schema?.properties || {};
            const systemSettingsRow = schema['ui:layout'][3];
            Object.keys(systemSettingsRow).forEach(key => {
                if (key.indexOf('ui:') === 0) return;
                if (!jsonSchemaProperties[key]) delete systemSettingsRow[key];
            });

            //rebuild the row so new_pm_id renders right next to the End date it belongs to//
            const reorderedRow = {};
            Object.keys(systemSettingsRow).forEach(key => {
                reorderedRow[key] = systemSettingsRow[key];
                if (key === 'timelog_end_at') {
                    reorderedRow.new_pm_id = {
                        xs: { span: 12, order: 1 }, sm: 4, md: 4, className: "mt-2",
                        doShow: ({ formData }) => !!formData?.timelog_end_at,
                    };
                }
            });
            schema['ui:layout'][3] = reorderedRow;
        }

        if (this.props.commonStore.config?.integration_details?.product !== payrollIntegrationOptions.TRIPLETEX) {
            schema['ui:layout'] = schema['ui:layout'].filter(ele => ele['ui:subtitle'] !== 'Integration Details')
        }


        if (mode === 'superadmins') {
            schema['ui:layout'][1] = schema['ui:layout'][2];
            schema['ui:layout'][2] = schema['ui:layout'][4];
            schema['ui:layout'].pop();
            schema['ui:layout'].pop();
            schema['ui:layout'].pop();
        }

        if (currentEntity?.readOnlyFields) {
            Object.keys(currentEntity.readOnlyFields).forEach(field => {
                if (!schema[field]) schema[field] = {};
                schema[field]['ui:readonly'] = true;
                schema[field]['ui:help'] = currentEntity?.readOnlyFields[field];
            });
        }
        
        const overtimeBankSettings = clientConfig?.basicRules?.overtimeBankSettings;
        const paidOvertimeBalanceConfigured = !!(overtimeBankSettings?.allowOvertimeBank && overtimeBankSettings?.allowPaidOvertimeBalanceManagement && overtimeBankSettings?.autoOvertimeBankRenewal);
        if (paidOvertimeBalanceConfigured) {
            const eligible = !!(overtimeBankSettings?.enablePaidOvertimeBalanceForAllEmployees || currentEntity?.payment_mode === 'fixed');
            if (!schema.paid_overtime_renewal_hours) schema.paid_overtime_renewal_hours = {};
            schema.paid_overtime_renewal_hours['ui:readonly'] = !eligible;
            schema.paid_overtime_renewal_hours['ui:help'] = eligible
                ? this.props.t("This value auto-renews into the employee's Overtime Bank paid overtime balance every year. Set to 0 to exclude this employee from auto-renewal. To manage their current balance, use the employee's Overtime Bank")
                : this.props.t('Paid overtime is only enabled for fixed payment employees, please contact administrator for updating configurations');
        }

        return schema;
    }

    handleAssignDocuments = (ids) => {
        this.setState({ assignedDocuments: ids });
    };

    updateSchema = (allFormData, oldData) => {
        const { commonStore, t, add } = this.props;
        if (!add && allFormData?.formData?.extra_payments?.split(',').length < oldData?.extra_payments?.split(',').length)
            commonStore.addNotification(t('Past data will remain unchanged, only future unregistered data will be affected. To make changes, manually edit and save the data'), null, 'error');

        if (allFormData.formData.payment_mode === oldData.payment_mode) return;
        const { schema } = allFormData;
        let _schema = cloneDeep(schema);
        if (_schema.properties.expected_fixed_hours && allFormData?.formData?.payment_mode)
            _schema.properties.expected_fixed_hours.readOnly = allFormData?.formData?.payment_mode === 'hourly' ? true : false
        this.setState({ schema: _schema })
    }

    calculateFixedHourlyRate = (formData) => {
        const { commonStore, userStore, t, add } = this.props;

        if (!this.isFormInitialized) {
            this.isFormInitialized = true;
            this.lastAllowAutoTimesheet = !!formData?.allow_user_for_auto_timesheet;
            this.lastEmployeeType = formData?.employee_type || null;
            this.lastCompanyShiftId = formData?.company_shift_id || null;
        } else {
            if (formData?.user_type === 'member' && formData?.allow_user_for_auto_timesheet && !this.lastAllowAutoTimesheet
                && !!commonStore?.config?.client?.data?.loginRules?.allowShiftToEnableDisableAutoTimesheet) {
                this.lastAllowAutoTimesheet = true;
                confirmAlert({
                    title: t('Note'),
                    message: t("Enabling 'Auto Time Recording' will also automatically grant this member the Schedule 'Create, Edit & Delete' and 'Edit Auto Time Recording' permissions as suggested settings. These permissions can be modified as needed from Users → More Actions → Bulk User Permissions."),
                    buttons: [{ label: t('OK') }],
                });
            } else if (!formData?.allow_user_for_auto_timesheet) {
                this.lastAllowAutoTimesheet = false;
            }

            if (!add && formData?.user_type === 'member' && formData?.employee_type === 'no_department' && this.lastEmployeeType !== 'no_department') {
                this.lastEmployeeType = 'no_department';
                confirmAlert({
                    title: t('Department change'),
                    message: t("You are changing the department to 'No Department' this will remove all roster permissions for this user if they have."),
                    buttons: [{ label: t('OK') }],
                });
            } else if (formData?.employee_type !== 'no_department') {
                this.lastEmployeeType = formData?.employee_type || null;
            }

            if (!add && (formData?.company_shift_id || null) !== this.lastCompanyShiftId) {
                this.lastCompanyShiftId = formData?.company_shift_id || null;
                confirmAlert({
                    title: t('Working shift change'),
                    message: t('Changing working shift may affect payment agreement.'),
                    buttons: [{ label: t('OK') }],
                });
            }
        }

        if (commonStore?.config?.client?.data?.basicRules?.hideSensitiveDetailsFromPM && userStore?.currentUser?.user_type === 'pm' && userStore?.currentUser?.id !== userStore.currentEntity.user?.id) return formData;

        if (formData['max_negative_hours_flexitime'])
            formData['max_negative_hours_flexitime'] = parseFloat(formData['max_negative_hours_flexitime'].toFixed(1));
        if (formData['max_positive_hours_flexitime'])
            formData['max_positive_hours_flexitime'] = parseFloat(formData['max_positive_hours_flexitime'].toFixed(1));

        if (formData['paid_overtime_renewal_hours'])
            formData['paid_overtime_renewal_hours'] = parseFloat(formData['paid_overtime_renewal_hours'].toFixed(2));

        const decimalPoint = this.props.commonStore?.config?.client?.data?.dateTimeRules?.currency_decimal_places
            ? this.props.commonStore.config.client.data.dateTimeRules.currency_decimal_places : 2;

        if (formData['work_time_agreement'])
            formData['work_time_agreement'] = parseFloat(formData['work_time_agreement'].toFixed(decimalPoint));

        if (formData.employee_percent && formData.work_time_agreement) {
            formData.calculated_employement_hours = (formData.employee_percent * formData.work_time_agreement) / 100;
        }

        if (formData?.payment_mode === 'fixed') {
            if (formData?.expected_fixed_hours === null || formData?.expected_fixed_hours === 0 ||
                formData?.hourly_rate === null || formData?.hourly_rate !== 0)
                formData['fixed_hourly_rate'] = 0
            if (formData?.hourly_rate > 0 && formData?.expected_fixed_hours > 0)
                formData['fixed_hourly_rate'] = parseFloat((formData.hourly_rate / formData.expected_fixed_hours).toFixed(decimalPoint))
            if (formData?.fixed_hourly_rate > 0 && formData.weekend_extra_payment_percent > 0)
                formData['calculated_weekly_hourly_payment'] = parseFloat((formData.fixed_hourly_rate + (formData.fixed_hourly_rate * formData.weekend_extra_payment_percent) / 100).toFixed(decimalPoint))
            else
                formData['calculated_weekly_hourly_payment'] = 0
        }
        if (formData?.payment_mode === 'hourly') {
            formData.expected_fixed_hours = 0;
            formData.fixed_hourly_rate = formData?.hourly_rate ? formData?.hourly_rate : 0;
            if (formData?.hourly_rate > 0)
                formData['calculated_weekly_hourly_payment'] = parseFloat((formData.hourly_rate + (formData.hourly_rate * formData.weekend_extra_payment_percent) / 100).toFixed(decimalPoint))
            else
                formData['calculated_weekly_hourly_payment'] = 0
        }
        return formData
    }

    render() {
        const { userStore, add, t, mode } = this.props;
        const { currentEntity, addPossibility } = userStore;
        const { schema } = this.state;
        if (this.props.userStore.loading) {
            return <LoadingSpinner />;
        }
        const addValid = !!add || addPossibility.allowed;

        let assignDocument = false;
        if (userStore.currentUser.user_type !== 'member' && userStore.currentUser.user_type !== 'super-admin' && !customerFeature('hide_documents')) {
            assignDocument = true;
        }

        return (
            <div className="primary-page profile-edit-form">
                {!addValid && <AdminAddLimitBeforeAdd />}

                {/* removing social and number page as per request - 28/10/2021 */}
                {/* {addValid && !canProceedEdit && <UserValidateBeforeAdd />} */}

                {/* removed caneditProceed condition- 28/10/2021 */}
                {/* {addValid && canProceedEdit && ( */}

                {addValid && (
                    <div>
                        <Row>
                            {currentEntity?.user?.user_type !== 'admin' &&
                                currentEntity.user.user_type !== 'super-admin' &&
                                !customerFeature('hide_digital_signature') && (
                                    <Col xs={12} sm={{ span: 6, offset: 6 }}
                                        md={{ span: 4, offset: 8 }}
                                        className={"text-end"}
                                    >
                                        <Button
                                            className="nextbtn btn-fill"
                                            fill
                                            wd
                                            icon
                                            onClick={this.employeeContract}
                                        >
                                            {t('Employee Contract')}
                                        </Button>
                                    </Col>
                                )}

                            <GenericForm
                                entity={currentEntity.user}
                                uiSchema={this.prepareSchema(
                                    defaultUiSchema,
                                    currentEntity.user,
                                    mode,
                                    userStore.currentUser
                                )}
                                updateSchema={(allFormData, formData) => this.updateSchema(allFormData, formData)}
                                onChange={this.calculateFixedHourlyRate}
                                schema={schema}
                                translationScope="forms.user"
                                onSave={(values, redirect) => this.onSave(values, redirect)}
                                listUrl={this.props.getListUrl}
                                isAdding={add}
                                setRef={this.setRef}
                                assignDocument={assignDocument}
                                handleAssignDocuments={(ids) => this.handleAssignDocuments(ids)}
                                customvalidate={(formData, errors) => this.validateUser(formData, errors)}
                                userContract={!customerFeature('hide_digital_signature')}
                                docAttachments={true}
                                goBack={this.props.goBack ? () => this.getBack() : false}
                            />
                        </Row>
                    </div>
                )}
                <Modal
                    show={this.state.showRosterConfirmModal}
                    size='lg'
                    onHide={this.handleRosterCancel}
                    backdrop="static"
                    keyboard={false}
                    centered
                >
                    <Modal.Header>
                        <Modal.Title>{this.props.t('Confirm Department change')}</Modal.Title>
                        <img
                            src={CloseModalIcon}
                            className="cursor-pointer"
                            alt='close_button'
                            onClick={this.handleRosterCancel}
                        />
                    </Modal.Header>
                    <Modal.Body>
                        <div className='fw-600'>{this.props.t('You are changing the department for this user.')}</div>
                        <div className='fw-600 text-danger'>{this.props.t('This user has active roster plans that are linked to the current department.')}</div>
                        <div className='fw-600 mb-2'>{this.props.t("Changing the department will update future plan and schedules based on the new department’s work rules.")}</div>
                        <div className='fw-600'>
                            {this.props.t("You can either apply the change on a specific date or change it immediately")}
                            <OverlayTrigger
                                placement="right"
                                overlay={
                                    <Tooltip id="tooltip-top">
                                        {t("Choosing “Apply change on a specific date” option, the update will take effect on the chosen date and will be locked for the time being.")}
                                    </Tooltip>
                                }
                            >
                                <i className="fa fa-info-circle text-muted ms-1" style={{ opacity: '0.5' }} aria-hidden="true"></i>
                            </OverlayTrigger>
                        </div>
                        {this.state.showDatePicker && (
                            <div className="mt-3 w-25">
                                <label className="d-block mb-1 fw-600">{this.props.t('Select effective date')}</label>
                                <DatePicker
                                    selected={this.state.implementDateOfDepartmentChange}
                                    onChange={(date) => this.setState({ implementDateOfDepartmentChange: date })}
                                    minDate={new Date(moment().add(1, 'day').format('YYYY-MM-DD'))}
                                    popperProps={{ strategy: 'fixed' }}
                                    popperPlacement="bottom-start"
                                />
                            </div>
                        )}
                    </Modal.Body>
                    <Modal.Footer>
                        <Button onClick={this.handleRosterCancel}>
                            {this.props.t('Cancel')}
                        </Button>
                        {!this.state.showDatePicker ? (
                            <Button className="btn-fill" fill onClick={() => this.setState({ showDatePicker: true })}>
                                {this.props.t('Apply Changes From')}
                            </Button>
                        ) : (
                            <Button
                                className="btn-fill"
                                fill
                                disabled={!this.state.implementDateOfDepartmentChange}
                                onClick={() => this.handleRosterConfirm(moment(this.state.implementDateOfDepartmentChange).format('YYYY-MM-DD'))}
                            >
                                {this.props.t('Confirm date and proceed')}
                            </Button>
                        )}
                        <Button className="btn-fill" fill disabled={this.state.showDatePicker} onClick={() => this.handleRosterConfirm(null)}>
                            {this.props.t('Yes, Change immediately')}
                        </Button>
                    </Modal.Footer>
                </Modal>

                <Modal
                    show={this.state.showWorkHoursConfirmModal}
                    size='lg'
                    onHide={this.handleWorkHoursCancel}
                    backdrop="static"
                    keyboard={false}
                    centered
                >
                    <Modal.Header>
                        <Modal.Title>{this.props.t('Department Work Rules Missing')}</Modal.Title>
                        <img
                            src={CloseModalIcon}
                            className="cursor-pointer"
                            alt='close_button'
                            onClick={this.handleWorkHoursCancel}
                        />
                    </Modal.Header>
                    <Modal.Body>
                        <div className='fw-600'>{this.props.t('You are changing the department for this user.')}</div>
                        <div className='fw-600 text-danger mb-2'>{this.props.t('The new department does not have any work hours rules configured.')}</div>
                        <div className='fw-600'>{this.props.t('You can proceed with the change, which will remove all future shifts and expire the current roster template.')}</div>
                        <div className='fw-600'>{this.props.t('Alternatively, contact the administrator to set up work hours rules for the new department before making this change.')}</div>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button onClick={this.handleWorkHoursCancel}>
                            {this.props.t('Cancel')}
                        </Button>
                        <Button className="btn-fill" fill onClick={this.handleWorkHoursConfirm}>
                            {this.props.t('Proceed (Remove Future Shifts)')}
                        </Button>
                    </Modal.Footer>
                </Modal>

                {this.state.showPmModal && (
                    <Modal
                        size="lg"
                        dialogClassName='modal-70w'
                        className={`right modal_revised`}
                        show={!!this.state.savedUserId && this.state.showPmModal}
                        onHide={this.handleClosePmModal}
                        backdrop="static"
                        keyboard={false}
                    >
                        <Modal.Header>
                            <Modal.Title className="w-100 text-center m-0">{this.props.t('Assign Manager to Member')}</Modal.Title>
                            <img
                                src={CloseModalIcon}
                                className="cursor-pointer"
                                alt='close_button'
                                onClick={this.handleClosePmModal}
                            />
                        </Modal.Header>
                        <Modal.Body style={{ minHeight: '80vh' }}>
                            <UserPmAssignForm
                                id={this.state.savedUserId}
                                updateManagerCount={this.updateManagerCount}
                            />
                        </Modal.Body>
                    </Modal>
                )}

                {this.state.showProjectModal && (
                    <Modal
                        size="lg"
                        dialogClassName='modal-70w'
                        className={`right modal_revised`}
                        show={!!this.state.savedUserId && this.state.showProjectModal}
                        onHide={this.handleCloseModal}
                        backdrop="static"
                        keyboard={false}
                    >
                        <Modal.Header>
                            <Modal.Title className="w-100 text-center m-0">{this.props.t('Assign Projects to Member')}</Modal.Title>
                            <img
                                src={CloseModalIcon}
                                className="cursor-pointer"
                                alt='close_button'
                                onClick={this.handleCloseModal}
                            />
                        </Modal.Header>
                        <Modal.Body style={{ minHeight: '80vh' }}>
                            <UserProjectAssignForm
                                id={this.state.savedUserId}
                                updateProjectCount={this.updateProjectCount}
                            />
                        </Modal.Body>
                    </Modal>
                )}
            </div>
        );
    }
}

export default inject('userStore', 'commonStore', 'documentStore', 'clientStore')(applicationRouter(withLocalization(observer(UsersForm))));
